/**
 * URL Configuration
 * Handles frontend and backend URL configuration for different deployment environments.
 * 
 * This is important when:
 * - Frontend is on a different domain than the backend (e.g., app.example.com vs api.example.com)
 * - Magic link emails need to redirect to the correct frontend URL
 * - OAuth callbacks need to return to the correct frontend URL
 * - API calls need to go to the correct backend URL
 */

/**
 * Get the frontend base URL
 * Used for:
 * - Magic link redirect URLs
 * - OAuth callback URLs
 * - Email links
 */
export const getFrontendUrl = (): string => {
  const explicitFrontendUrl = import.meta.env.VITE_FRONTEND_URL?.trim();
  if (explicitFrontendUrl) {
    return explicitFrontendUrl.replace(/\/$/, '');
  }

  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  if (import.meta.env.VITE_VERCEL_URL) {
    return `https://${import.meta.env.VITE_VERCEL_URL}`;
  }

  if (import.meta.env.CF_PAGES_URL) {
    return import.meta.env.CF_PAGES_URL;
  }

  return 'http://localhost:3000';
};

/**
 * Get the backend API base URL
 * Used for:
 * - All API calls from frontend to backend
 */
export const getBackendUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const isLocalHost =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.startsWith('192.168.') ||
      hostname.startsWith('10.') ||
      /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname);
    if (isLocalHost) {
      const allowRemoteApi =
        String(import.meta.env.VITE_ALLOW_REMOTE_API_IN_LOCAL || '').toLowerCase() === 'true';
      const configuredApi = (import.meta.env.VITE_API_URL || '').trim();
      const configuredIsRemote =
        configuredApi.startsWith('https://') &&
        !configuredApi.includes('localhost') &&
        !configuredApi.includes('127.0.0.1');

      if (allowRemoteApi && configuredApi.length > 0) {
        return configuredApi.replace(/\/$/, '');
      }

      if (configuredIsRemote) {
        console.warn('[URL Config] Ignoring remote VITE_API_URL in local dev. Using same-origin /api');
      }

      return '';
    }
  }

  const explicitBackendUrl = import.meta.env.VITE_API_URL?.trim();
  if (explicitBackendUrl && explicitBackendUrl !== '/api') {
    return explicitBackendUrl.replace(/\/$/, '');
  }

  if (typeof window !== 'undefined' && window.ENV?.API_URL) {
    const runtimeApiUrl = window.ENV.API_URL.trim();
    if (runtimeApiUrl && runtimeApiUrl !== '/api') {
      return runtimeApiUrl.replace(/\/$/, '');
    }
  }

  return '';
};

/**
 * Build a frontend route URL
 * @param path - The route path (e.g., '/auth/magic-link')
 * @returns Full URL (e.g., 'https://app.example.com/auth/magic-link')
 */
export const getFrontendRoute = (path: string): string => {
  const baseUrl = getFrontendUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

/**
 * Build a backend API URL
 * @param path - The API path (e.g., 'auth/complete' or '/api/auth/complete')
 * @returns Full URL (e.g., 'https://api.example.com/api/auth/complete')
 */
export const getApiRoute = (path: string): string => {
  const baseUrl = getBackendUrl();
  let fullPath = path.startsWith('/') ? path : `/${path}`;
  if (!fullPath.startsWith('/api')) {
    fullPath = '/api' + fullPath;
  }
  // If baseUrl is empty, return relative URL
  if (!baseUrl) {
    return fullPath;
  }
  return `${baseUrl}${fullPath}`;
};

/**
 * URL Configuration object for easy access
 */
export const URL_CONFIG = {
  get frontend() {
    return getFrontendUrl();
  },
  get backend() {
    return getBackendUrl();
  },
  routes: {
    // Auth routes
    magicLinkCallback: '/auth/magic-link',
    signIn: '/signin',
    signUp: '/auth/signup',
    forgotPassword: '/auth/forgot-password',
    // Main routes
    home: '/',
    dashboard: '/dashboard',
  },
} as const;

// Type declarations for global ENV object
declare global {
  interface Window {
    ENV?: {
      API_URL?: string;
      FRONTEND_URL?: string;
      SUPABASE_URL?: string;
      SUPABASE_ANON_KEY?: string;
      [key: string]: string | undefined;
    };
  }
}
